import React, { useState, useEffect } from 'react';
import { Container, Spinner } from 'reactstrap';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { toast } from 'react-toastify';
import Breadcrumb from 'components/App/Breadcrumb';
import Meta from 'components/App/Meta';
import DownloadIcon from 'assets/images/download.svg';
import CopyIcon from 'assets/images/copy-icon.svg';
import images from 'assets/images';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import Button from 'components/App/Button';
import { BOOKING_STATUS, LOCATIONS, themeColor } from 'constants/index';
import {
  getGlobalInvoiceDetail,
  cleanupGlobalInvoiceDetail,
  payInvoice,
  exportInvoice,
} from 'store/actions';
import styles from './PaymentDetail.module.scss';

const breadcrumbList = [
  {
    name: 'Quản lý thanh toán',
    isActive: false,
    link: '/manager-payment',
  },
  {
    name: 'Chi tiết hoá đơn',
    isActive: true,
    link: '',
  },
];

function PaymentDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    globalInvoiceDetail,
    globalInvoiceDetailLoading,
    payLoading,
    exportInvoiceLoading,
  } = useSelector((state) => state.invoice);

  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getGlobalInvoiceDetail(id));
    }
    return () => {
      dispatch(cleanupGlobalInvoiceDetail());
    };
  }, [dispatch, id]);

  const handleCopy = (text) => {
    if (navigator.clipboard && text && text !== 'Chưa có thông tin') {
      navigator.clipboard.writeText(text);
      toast.success(`Đã sao chép: ${text}`);
    }
  };

  const handleOpenConfirmModal = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsOpenConfirmModal(true);
  };

  const handleConfirmTogglePaid = () => {
    const placeId = globalInvoiceDetail?.place?._id;
    dispatch(
      payInvoice({
        id,
        placeId,
        callback: () => {
          setIsOpenConfirmModal(false);
          dispatch(getGlobalInvoiceDetail(id));
        },
      }),
    );
  };

  const handleExportFile = () => {
    dispatch(
      exportInvoice({
        id,
        callback: (data) => {
          const downloadUrl = data?.payload?.downloadUrl || data?.downloadUrl;
          if (downloadUrl) {
            window.open(downloadUrl, '_blank');
            return;
          }

          if (data) {
            const blob = data instanceof Blob ? data : new Blob([data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `invoice_${id}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
          }
        },
      }),
    );
  };

  const renderRoleBadge = (role) => {
    switch (role?.toUpperCase()) {
      case 'TOURIST':
        return <span className={`${styles.RoleBadge} ${styles.Guest}`}>Du khách</span>;
      case 'MEMBER':
        return <span className={`${styles.RoleBadge} ${styles.Member}`}>Thành viên</span>;
      case 'GUEST':
        return <span className={`${styles.RoleBadge} ${styles.Guest}`}>Du khách</span>;
      case 'AGENT':
        return <span className={`${styles.RoleBadge} ${styles.Agent}`}>Đại lý</span>;
      case 'AMBASSADOR':
        return <span className={`${styles.RoleBadge} ${styles.Ambassador}`}>Đại sứ</span>;
      default:
        return <span className={`${styles.RoleBadge} ${styles.Member}`}>{role || 'Thành viên'}</span>;
    }
  };

  const bankName = globalInvoiceDetail?.place?.bankAccount?.bankName || 'Chưa có thông tin';
  const accountNumber = globalInvoiceDetail?.place?.bankAccount?.accountNumber || 'Chưa có thông tin';
  const accountHolder = globalInvoiceDetail?.place?.bankAccount?.accountName || 'Chưa có thông tin';
  const paymentDate = globalInvoiceDetail?.paidAt ? moment(globalInvoiceDetail.paidAt).format('DD/MM/YYYY') : '-';
  const totalAmount = globalInvoiceDetail?.totalAmount ? `${globalInvoiceDetail.totalAmount.toLocaleString('vi-VN')} VNĐ` : '0 VNĐ';
  const isPaid = Boolean(globalInvoiceDetail?.isPaid);
  const userBookings = globalInvoiceDetail?.userBookings || [];
  const userBookingCount = globalInvoiceDetail?.userBookingCount ?? userBookings.length;

  const renderBookingsTableContent = () => {
    if (globalInvoiceDetailLoading) {
      return (
        <tr>
          <td colSpan="6" className="text-center p-4">
            <Spinner size="sm" color="primary" />
            {' '}
            Đang tải dữ liệu...
          </td>
        </tr>
      );
    }

    if (userBookings.length === 0) {
      return (
        <tr>
          <td colSpan="6" className="text-center p-4 text-muted">
            Chưa có đơn đặt phòng nào
          </td>
        </tr>
      );
    }

    let placeCode = '-';
    if (globalInvoiceDetail?.place?.placeId) {
      placeCode = `#${globalInvoiceDetail.place.placeId}`;
    } else if (globalInvoiceDetail?.place?._id) {
      placeCode = `#${globalInvoiceDetail.place._id.slice(-8)}`;
    }
    const placeName = globalInvoiceDetail?.place?.name || 'Chưa có thông tin';

    return userBookings.map((item, index) => {
      const roomInfo = item.rooms && item.rooms.length > 0
        ? item.rooms.map((r) => `${r.name} x${r.roomQuantity}`).join(', ')
        : '-';
      const formattedAmount = item.amount ? `VND ${item.amount.toLocaleString('vi-VN')}` : 'VND 0';
      let bookingCode = '-';
      if (item.orderId) {
        bookingCode = `#${item.orderId}`;
      } else if (item.booking) {
        bookingCode = `#${item.booking.slice(-8)}`;
      }

      const targetBookingId = item.booking || item._id;

      return (
        <tr key={item._id || item.booking || index}>
          <td className={`${styles.BookingCode} ${styles.ColBookingCode}`}>
            {targetBookingId ? (
              <Link
                to={LOCATIONS.MANAGER_BOOKING_DETAIL.replace(':id', targetBookingId)}
                className={styles.BookingCodeLink}
              >
                {bookingCode}
              </Link>
            ) : (
              bookingCode
            )}
          </td>
          <td className={styles.ColCustomer}>
            <div className={styles.CustomerWrapper}>
              <span className={styles.CustomerName}>{item.user?.fullName || 'Khách hàng'}</span>
              {renderRoleBadge(item.user?.level)}
            </div>
          </td>
          <td className={styles.ColPlace}>
            <div className={styles.PlaceCode}>{placeCode}</div>
            <div className={styles.PlaceName}>{placeName}</div>
          </td>
          <td className={styles.ColStatus}>
            <span
              className={`${styles.StatusBadge} ${
                (item.bookingStatus
                  ? ['SUCCESS', 'PAID', 'APPROVED'].includes(item.bookingStatus)
                  : item.isPaid)
                  ? styles.Completed
                  : styles.NotBooked
              }`}
            >
              {BOOKING_STATUS[item.bookingStatus]?.label || item.bookingStatus || (item.isPaid ? 'Đã hoàn thành' : 'Chưa đặt phòng')}
            </span>
          </td>
          <td className={`${styles.RoomInfo} ${styles.ColRoom}`}>{roomInfo}</td>
          <td className={`${styles.Amount} ${styles.ColAmount}`}>{formattedAmount}</td>
        </tr>
      );
    });
  };

  let modalText = (
    <>
      Bạn có chắc chắn muốn xác nhận
      <br />
      Đã thanh toán cho hoá đơn này?
    </>
  );
  if (isPaid) {
    modalText = (
      <>
        Bạn có chắc chắn muốn chuyển
        <br />
        trạng thái hoá đơn này thành Chưa thanh toán?
      </>
    );
  }

  const placeId = globalInvoiceDetail?.place?.placeId
    || (globalInvoiceDetail?.place?._id ? globalInvoiceDetail.place._id.slice(-8) : '');
  const placeName = globalInvoiceDetail?.place?.name || '';
  const pageTitle = (placeId || placeName)
    ? [placeId, placeName].filter(Boolean).join(' - ')
    : 'Chi tiết hoá đơn';

  return (
    <>
      <div className={`page-content ${styles.Page}`}>
        <Meta name={pageTitle} />
        <Container fluid>
          <Breadcrumb
            pageTitle={pageTitle}
            breadcrumbList={breadcrumbList}
          />

          <div className={styles.TopCard}>
            <div className={styles.TopInfoContainer}>
              <div className={styles.LeftInfoGroup}>
                <div className={styles.InfoItem}>
                  <div className={styles.InfoLabel}>Tên ngân hàng</div>
                  <div className={styles.InfoValue}>
                    <span>{bankName}</span>
                    <button
                      type="button"
                      className={styles.CopyBtn}
                      onClick={() => handleCopy(bankName)}
                      title="Sao chép"
                      aria-label="Sao chép tên ngân hàng"
                    >
                      <img src={CopyIcon} alt="copy" width={15} height={17} />
                    </button>
                  </div>
                </div>

                <div className={styles.InfoItem}>
                  <div className={styles.InfoLabel}>Số tài khoản</div>
                  <div className={styles.InfoValue}>
                    <span>{accountNumber}</span>
                    <button
                      type="button"
                      className={styles.CopyBtn}
                      onClick={() => handleCopy(accountNumber)}
                      title="Sao chép"
                      aria-label="Sao chép số tài khoản"
                    >
                      <img src={CopyIcon} alt="copy" width={15} height={17} />
                    </button>
                  </div>
                </div>

                <div className={styles.InfoItem}>
                  <div className={styles.InfoLabel}>Chủ thẻ</div>
                  <div className={styles.InfoValue}>
                    <span>{accountHolder}</span>
                    <button
                      type="button"
                      className={styles.CopyBtn}
                      onClick={() => handleCopy(accountHolder)}
                      title="Sao chép"
                      aria-label="Sao chép tên chủ thẻ"
                    >
                      <img src={CopyIcon} alt="copy" width={15} height={17} />
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.Divider} />

              <div className={styles.RightInfoGroup}>
                <div className={styles.InfoItem}>
                  <div className={styles.InfoLabel}>Ngày thanh toán</div>
                  <div className={styles.InfoValue}>
                    <span>{paymentDate}</span>
                  </div>
                </div>

                <div className={styles.InfoItem}>
                  <div className={styles.InfoLabel}>Tổng số tiền</div>
                  <div className={styles.InfoValue}>
                    <span>{totalAmount}</span>
                  </div>
                </div>

                <Button
                  disabled={isPaid}
                  loading={payLoading}
                  name="Đang xử lý"
                  onClick={handleOpenConfirmModal}
                  color={isPaid ? themeColor.SUCCESS : themeColor.PRIMARY}
                  className="text-button"
                >
                  {isPaid ? 'Đã thanh toán' : 'Xác nhận thanh toán'}
                </Button>
              </div>
            </div>
          </div>

          <div className={styles.BottomCard}>
            <div className={styles.TableHeaderRow}>
              <div className={styles.TableTitle}>{`${userBookingCount} đơn đặt phòng`}</div>
              <button
                type="button"
                className={styles.ExportBtn}
                onClick={handleExportFile}
                disabled={exportInvoiceLoading}
              >
                {exportInvoiceLoading ? (
                  <Spinner size="sm" color="primary" />
                ) : (
                  <>
                    <img src={DownloadIcon} alt="export" width={14} height={14} />
                    <span>Xuất file</span>
                  </>
                )}
              </button>
            </div>

            <div className={styles.TableContainer}>
              <table className={styles.Table}>
                <thead>
                  <tr>
                    <th className={styles.ColBookingCode}>Mã đặt phòng</th>
                    <th className={styles.ColCustomer}>Khách hàng</th>
                    <th className={styles.ColPlace}>Mã chỗ nghỉ | Tên chỗ nghỉ</th>
                    <th className={styles.ColStatus}>Trạng thái</th>
                    <th className={styles.ColRoom}>Tên phòng x Số lượng phòng</th>
                    <th className={styles.ColAmount}>Tổng tiền</th>
                  </tr>
                </thead>
                <tbody>
                  {renderBookingsTableContent()}
                </tbody>
              </table>
            </div>
          </div>
          <ModalRequestAction
            isOpen={isOpenConfirmModal}
            icon={images.modalWarning.default || images.modalWarning}
            textRequest={modalText}
            onCloseModal={() => setIsOpenConfirmModal(false)}
            onConfirm={handleConfirmTogglePaid}
            isLoadingConfirm={payLoading}
          />
        </Container>
      </div>
    </>
  );
}

export default PaymentDetail;
